import { CSSProperties, Dispatch, MouseEventHandler, ReactNode, SetStateAction, useEffect } from 'react';
import { Box } from '@mui/material';
import type { ApiOrganizationProfile } from '@agunity/api-v4/build/data-contracts';

import { AgColor } from '../../../Styles/AgColor';
import AgTypography from '../../../Styles/AgTypography';
import AgProfileHeader from '../../PageHeaders/AgProfileHeader';
import AgProfilePageNavigation from '../../Navigations/AgProfilePageNavigation';
import type { AgTabsDataProps } from '../../AgTab';

import { useAppDispatch } from '../../../../redux/hooks';
import { setIsToastOpen_navFromAddMember } from '../../../../redux/slices/member';
import { setIsToastOpen_navFromAddStaff } from '../../../../redux/slices/staff';
import { setIsToastOpen_navFromAddOrganization } from '../../../../redux/slices/organizations';

export interface AgProfilePageProps {
  headerHasPersonalDetailsError?: boolean;
  headerHasMemberProfileError?: boolean;
  headerTabsData?: AgTabsDataProps[][];
  headerButtonLabel?: string;
  headerButtonDisabled?: boolean;
  headerButtonHide?: boolean;
  headerCardTitle?: string;
  headerCardName?: string;
  headerCardInfo?: string;
  headerButtonOnClick?: MouseEventHandler;
  isHeaderButtonLoading?: boolean;
  navButtonOnClick?: MouseEventHandler<HTMLButtonElement>;
  navHideInfoCard?: boolean;
  navHideAddButton?: boolean;
  navInfoCardTitle?: string;
  navInfoCardMessage?: string;
  navInfoCardChildren?: ReactNode;
  navAccountImg?: string;
  navAccountName?: string;
  navAccountID?: string;
  navAccountVillage?: string;
  navAccountEmail?: string;
  navAccountPhone?: string;
  navInactive?: boolean;
  navAccountUserType?: number;
  navIsSelected?: boolean;
  navMemberData?: ApiOrganizationProfile[];
  navOrgData?: ApiOrganizationProfile[];
  navHeight?: string;
  style?: CSSProperties;
  navMemberProfile?: string | number;
  resetPersonalDetailsToOriginal?: () => void;
  resetMemberDetailsToOriginal?: () => void;
  selectedCard?: number;
  setSelectedCard?: Dispatch<SetStateAction<number>>;
  isDirty?: boolean;
  openDirtyModal?: boolean;
  setOpenDirtyModal?: Dispatch<SetStateAction<boolean>>;
  profileIndex?: number;
  setProfileIndex?: Dispatch<SetStateAction<number>>;
  profileUnsavedModal?: ReactNode;
  viewAvatarModal?: ReactNode;
  showSteps?: boolean;
  setValueForSaveBtn?: Dispatch<SetStateAction<number>>;
  navigationHeaderTitle1?: string;
  navigationHeaderTitle2?: string;
  accountProfileCardTitle?: string;
  accountProfileCardIdLabel?: string;
  updatedTabValue?: number;
  stepsLabels?: string[];
  fixedContent?: boolean;
  hideOrgDataAndShowSettings?: boolean;
  // eslint-disable-next-line no-unused-vars
  onTabChange?: (value: number) => void;
}

const AgProfilePage = (
  {
    headerHasPersonalDetailsError,
    headerHasMemberProfileError,
    headerTabsData,
    headerButtonLabel,
    headerButtonDisabled,
    headerButtonHide,
    headerCardTitle,
    headerCardName,
    headerCardInfo,
    headerButtonOnClick,
    isHeaderButtonLoading,
    navButtonOnClick,
    navHideInfoCard,
    navHideAddButton,
    navInfoCardTitle,
    navInfoCardMessage,
    navInfoCardChildren,
    navAccountImg,
    navAccountName,
    navAccountUserType,
    navAccountID,
    navAccountVillage,
    navAccountEmail,
    navAccountPhone,
    navInactive,
    navMemberData,
    navOrgData,
    navMemberProfile,
    resetPersonalDetailsToOriginal,
    resetMemberDetailsToOriginal,
    setSelectedCard,
    selectedCard,
    isDirty,
    openDirtyModal,
    setOpenDirtyModal,
    profileIndex,
    setProfileIndex,
    profileUnsavedModal,
    viewAvatarModal,
    style,
    showSteps,
    setValueForSaveBtn,
    navigationHeaderTitle1,
    navigationHeaderTitle2,
    accountProfileCardTitle,
    accountProfileCardIdLabel,
    updatedTabValue,
    stepsLabels,
    fixedContent,
    hideOrgDataAndShowSettings,
    onTabChange,
  }: AgProfilePageProps
) => {
  const dispatch = useAppDispatch();
  
  const tabsData: undefined | AgTabsDataProps[] =
    selectedCard === 0 || !selectedCard
      ? headerTabsData && headerTabsData[0]
      : (
        selectedCard &&
        navMemberData &&
        selectedCard - 1 >= 0 &&
        selectedCard - 1 < navMemberData?.length
      ) || (fixedContent && selectedCard === 1)
        ? headerTabsData && headerTabsData[1]
        : headerTabsData && headerTabsData[2];

  useEffect(() => {
    // Reset all alerts upon landing on the Profile Page
    dispatch(setIsToastOpen_navFromAddMember(false));
    dispatch(setIsToastOpen_navFromAddStaff(false));
    dispatch(setIsToastOpen_navFromAddOrganization(false));
  }, [dispatch]);

  return (
    <Box
      sx={{
        display: 'flex',
        maxWidth: '880px',
        backgroundColor: AgColor.grey0,
        borderRadius: '8px',
        boxShadow: '0px 1px 2px 0px #0000000D',
        ...style,
      }}
    >
      <AgProfilePageNavigation
        buttonOnClick={navButtonOnClick}
        infoCardTitle={navInfoCardTitle}
        infoCardMessage={navInfoCardMessage}
        infoCardChildren={navInfoCardChildren}
        accountUserType={navAccountUserType}
        accountImg={navAccountImg}
        accountName={navAccountName}
        accountID={navAccountID}
        accountVillage={navAccountVillage}
        accountEmail={navAccountEmail}
        accountPhone={navAccountPhone}
        inactive={navInactive}
        memberData={navMemberData}
        orgData={navOrgData}
        memberProfile={navMemberProfile}
        selectedCard={selectedCard}
        setSelectedCard={setSelectedCard}
        hideInfoCard={navHideInfoCard}
        hideAddButton={navHideAddButton}
        resetPersonalDetailsToOriginal={resetPersonalDetailsToOriginal}
        resetMemberDetailsToOriginal={resetMemberDetailsToOriginal}
        isDirty={isDirty}
        openDirtyModal={openDirtyModal}
        setOpenDirtyModal={setOpenDirtyModal}
        profileIndex={profileIndex}
        setProfileIndex={setProfileIndex}
        profileUnsavedModal={profileUnsavedModal}
        viewAvatarModal={viewAvatarModal}
        showSteps={showSteps}
        navigationHeaderTitle1={navigationHeaderTitle1}
        navigationHeaderTitle2={navigationHeaderTitle2}
        accountProfileCardTitle={accountProfileCardTitle}
        accountProfileCardIdLabel={accountProfileCardIdLabel}
        stepsLabels={stepsLabels}
        hideOrgDataAndShowSettings={hideOrgDataAndShowSettings}
      />
      <AgTypography
        style={{
          flex: 1
        }}
      >
        <AgProfileHeader
          hasPersonalDetailsError={headerHasPersonalDetailsError}
          hasMemberProfileError={headerHasMemberProfileError}
          tabsData={tabsData || []}
          buttonLabel={headerButtonLabel}
          buttonDisabled={headerButtonDisabled}
          buttonHide={headerButtonHide}
          cardTitle={headerCardTitle}
          cardID={headerCardName}
          cardInfo={headerCardInfo}
          onClick={headerButtonOnClick}
          isButtonLoading={isHeaderButtonLoading}
          setValueForSaveBtn={setValueForSaveBtn}
          updatedTabValue={updatedTabValue}
          onTabChange={onTabChange}
        />
      </AgTypography>
    </Box>
  );
};

export default AgProfilePage;